import { useEffect } from 'react';
import { ButtonHeadless, Modal, Spin } from '@wajeez-web/components/ui';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormAutocompleteV2 } from '@wajeez-web/components/form-builder';
import { Server } from '@wajeez/wajeez.microservices';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { BiLoaderAlt } from 'react-icons/bi';
import {
  ProductGroupsActions,
  PromoCodesActions,
  useProductDispatch,
  useProductSelector,
} from '@wajeez-web/stores/product';

type Input = Server.AssignPromoCodeToProductGroupInput;

interface AssignPromoCodeToProductGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: () => void | Promise<void>;
  id?: number;
}

export const AssignPromoCodeToProductGroupModal = ({
  isOpen,
  onClose,
  onSubmit,
  id,
}: AssignPromoCodeToProductGroupModalProps) => {
  const productDispatch = useProductDispatch();

  const assignPromoCodeStatus = useProductSelector(
    ({ promoCodes }) => promoCodes.assignPromoCodeToProductGroup.status
  );
  const promoCodeById = useProductSelector(
    ({ promoCodes }) => promoCodes.promoCodeById
  );

  const productGroupsLookups = useProductSelector(
    ({ productGroups }) => productGroups.productGroupsLookups
  );

  const { handleSubmit, control, reset } = useForm<Input>({
    defaultValues: {},
  });

  const onFormSubmit: SubmitHandler<Input> = async (data) => {
    const res = await productDispatch(
      PromoCodesActions.assignPromoCodeToProductGroupAsync({
        promoCodeId: id,
        ...data,
      })
    );

    if (res.meta.requestStatus === 'fulfilled') {
      onSubmit && (await onSubmit());
    }
  };

  useEffect(() => {
    if (promoCodeById.data) {
      reset(promoCodeById.data);
    }
  }, [promoCodeById.data]);

  useEffect(() => {
    if (id) {
      productDispatch(PromoCodesActions.getPromoCodeByIdAsync(id));
    }

    return () => {
      productDispatch(PromoCodesActions.clearPromoCode());
    };
  }, [id, productDispatch]);

  useEffect(() => {
    productDispatch(ProductGroupsActions.getProductGroupsLookupsAsync({}));
  }, [productDispatch]);

  const isLoading =
    productGroupsLookups.status === 'loading' ||
    promoCodeById.status === 'loading';

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Assign Promo Code to Product Group"
      alertTarget="modal"
      fontClassName="font-[Tajawal]"
    >
      <div className="w-full">
        {isLoading ? (
          <div className="w-full min-h-[200px] flex justify-center items-center">
            <Spin />
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onFormSubmit)}
            noValidate
            className="w-full flex flex-col justify-start items-start"
          >
            <div className="w-[300px] flex flex-col justify-start items-start space-y-2">
              <div className="w-full">
                <FormAutocompleteV2
                  name="productGroupId"
                  control={control}
                  fieldMeta={{
                    fieldType: Server.FieldType.Select,
                    fieldOptions: {
                      selectOptions: productGroupsLookups.data.map((p) => {
                        return {
                          value: p.id?.toString() || '',
                          label: p.groupName || '',
                        };
                      }),
                    },
                    required: true,
                    label: 'Product Group',
                  }}
                  required
                />
              </div>
            </div>
            <div className="mt-6 w-full flex justify-end items-center space-s-2">
              <div className="w-[70px]">
                <ButtonHeadless
                  bgStyle="bg-warmGray-300"
                  borderStyle="border-none"
                  paddingYStyle="py-1"
                  action={onClose}
                >
                  <div className="text-warmGray-600 text-[16px] font-medium">
                    <ReturnTranslatedText
                      text="buttons.Cancel"
                      origintext="Cancel"
                    />
                  </div>
                </ButtonHeadless>
              </div>
              <div className="w-[70px]">
                <ButtonHeadless
                  type="submit"
                  borderStyle="border-none"
                  paddingYStyle="py-1"
                >
                  <div className="text-white text-[16px] font-medium flex justify-center items-center space-s-2">
                    <ReturnTranslatedText
                      text="buttons.Save"
                      origintext="Save"
                    />
                    {assignPromoCodeStatus === 'loading' && (
                      <BiLoaderAlt className="animate-spin w-5" />
                    )}
                  </div>
                </ButtonHeadless>
              </div>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};
